import { useState } from 'react'
import { format } from 'date-fns'
import {
  ArrowRight,
  Building2,
  CalendarDays,
  CheckCircle2,
  PencilLine,
  Quote,
  XCircle,
} from 'lucide-react'
import { Modal, Button, Badge, DateInput, TimeInput } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { Avatar } from '@/components/common/Avatar'
import { useTimeCorrectionRequestById, useApproveTimeCorrectionRequest, useRejectTimeCorrectionRequest } from '@/features/attendance/hooks/useTimeCorrection'
import { usePermissions } from '@/hooks/usePermissions'
import { formatDate, formatDateTime, formatRelative, userSubline } from '@/lib/formatters'
import { cn } from '@/lib/cn'
import type {
  ApproveTimeCorrectionRequestDto,
  TimeCorrectionRequest,
} from '@/features/attendance/types/timeCorrection.types'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import {
  TIME_CORRECTION_STATUS_LABEL,
  TIME_CORRECTION_STATUS_VARIANT,
  formatMinutes,
  getOriginalAttendance,
  getRequester,
  getReviewer,
  minutesBetween,
  personDisplayName,
  personRoleLine,
  reasonCodeLabel,
} from '@/features/attendance/utils/timeCorrectionDisplay'

/** Clock times carry the day too, since a corrected shift can cross midnight. */
const CLOCK_FORMAT = 'EEE d MMM, p'
const CLOCK_FIELD_GRID =
  'grid grid-cols-1 gap-2.5 sm:grid-cols-[6.5rem_minmax(0,1fr)_8rem] sm:items-start'
/** Hint text stacks under the buttons on phones; beside them it takes the leftover width. */
const FOOTER_ROW =
  'flex w-full flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4'
const FOOTER_HINT = 'min-w-0 text-xs text-gray-500 sm:flex-1 dark:text-gray-400'

type ReviewMode = 'review' | 'adjust' | 'reject'

interface ClockDraft {
  inDate: string
  inTime: string
  outDate: string
  outTime: string
}

function splitLocal(value?: string): { date: string; time: string } {
  if (!value) return { date: '', time: '' }
  const d = new Date(value)
  return { date: format(d, 'yyyy-MM-dd'), time: format(d, 'HH:mm') }
}

function joinLocal(date: string, time: string): Date | null {
  if (!date || !time) return null
  const d = new Date(`${date}T${time}:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

/** The times approving as-is would write: requested where given, else what's recorded. */
function effectiveTimes(request: TimeCorrectionRequest): { clockIn?: string; clockOut?: string } {
  const original = getOriginalAttendance(request)
  return {
    clockIn: request.requestedClockIn ?? original?.clockIn,
    clockOut: request.requestedClockOut ?? original?.clockOut,
  }
}

function initialDraft(request: TimeCorrectionRequest): ClockDraft {
  const { clockIn, clockOut } = effectiveTimes(request)
  const a = splitLocal(clockIn)
  const b = splitLocal(clockOut)
  return { inDate: a.date, inTime: a.time, outDate: b.date, outTime: b.time }
}

function validateDraft(draft: ClockDraft): { in?: string; out?: string } {
  const clockIn = joinLocal(draft.inDate, draft.inTime)
  const clockOut = joinLocal(draft.outDate, draft.outTime)
  const errors: { in?: string; out?: string } = {}
  if (!clockIn) errors.in = 'Enter a clock-in date and time'
  if (!clockOut) errors.out = 'Enter a clock-out date and time'
  else if (clockIn && clockOut.getTime() <= clockIn.getTime())
    errors.out = 'Clock-out must be after clock-in'
  return errors
}

export interface TimeCorrectionRequestDetailModalProps {
  companyId: string
  requestId: string | null
  /** Shown in the employee header; the modal has no company lookup of its own. */
  companyName?: string
  onClose: () => void
  onActionComplete?: () => void
}

export function TimeCorrectionRequestDetailModal({
  companyId,
  requestId,
  companyName,
  onClose,
  onActionComplete,
}: TimeCorrectionRequestDetailModalProps): React.ReactElement | null {
  const [rejectReason, setRejectReason] = useState('')
  const [mode, setMode] = useState<ReviewMode>('review')
  const [draft, setDraft] = useState<ClockDraft | null>(null)
  const [reviewNote, setReviewNote] = useState('')
  const { data, isLoading, error } = useTimeCorrectionRequestById(companyId, requestId)
  const approveMutation = useApproveTimeCorrectionRequest()
  const rejectMutation = useRejectTimeCorrectionRequest()
  const { can } = usePermissions()
  const request = data?.data

  if (!requestId) return null

  // The API checks time_corrections, not attendance. Approve and reject are
  // granted separately, and either one makes the viewer a reviewer.
  const mayApprove = can('time_corrections', 'approve')
  const mayReject = can('time_corrections', 'reject')
  const canDecide = (mayApprove || mayReject) && request?.status === 'pending'
  const isPending = approveMutation.isPending || rejectMutation.isPending
  const draftErrors = draft ? validateDraft(draft) : {}
  const draftValid = !!draft && !draftErrors.in && !draftErrors.out
  const draftChanged =
    !!draft && !!request && JSON.stringify(draft) !== JSON.stringify(initialDraft(request))

  const backToReview = () => {
    setMode('review')
    setRejectReason('')
    setDraft(null)
    setReviewNote('')
  }

  const startAdjust = () => {
    if (!request) return
    setDraft(initialDraft(request))
    setMode('adjust')
  }

  const handleClose = () => {
    backToReview()
    onClose()
  }

  const handleApprove = () => {
    if (!companyId || !requestId || !request) return
    let body: ApproveTimeCorrectionRequestDto | undefined
    if (mode === 'adjust' && draft) {
      // Only send the ends the reviewer actually moved; the rest stay as requested.
      const initial = initialDraft(request)
      const clockIn = joinLocal(draft.inDate, draft.inTime)
      const clockOut = joinLocal(draft.outDate, draft.outTime)
      body = {
        ...(clockIn && (draft.inDate !== initial.inDate || draft.inTime !== initial.inTime)
          ? { approvedClockIn: clockIn.toISOString() }
          : {}),
        ...(clockOut && (draft.outDate !== initial.outDate || draft.outTime !== initial.outTime)
          ? { approvedClockOut: clockOut.toISOString() }
          : {}),
        ...(reviewNote.trim() ? { reviewNote: reviewNote.trim() } : {}),
      }
    }
    approveMutation.mutate(
      { companyId, requestId, body },
      {
        onSuccess: () => {
          handleClose()
          onActionComplete?.()
        },
      }
    )
  }

  const handleRejectSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = rejectReason.trim()
    if (!companyId || !requestId || !trimmed) return
    rejectMutation.mutate(
      { companyId, requestId, body: { rejectionReason: trimmed } },
      {
        onSuccess: () => {
          handleClose()
          onActionComplete?.()
        },
      }
    )
  }

  const footer = canDecide ? (
    mode === 'adjust' ? (
      <div className={FOOTER_ROW}>
        <p className={FOOTER_HINT}>
          {draftChanged
            ? 'The attendance record gets your times, not the requested ones.'
            : 'No changes yet. This approves the requested times.'}
        </p>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0">
          <Button
            type="button"
            variant="secondary"
            onClick={backToReview}
            disabled={isPending}
            className="whitespace-nowrap"
          >
            Back
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleApprove}
            loading={approveMutation.isPending}
            disabled={isPending || !draftValid}
            className="whitespace-nowrap"
          >
            {draftChanged ? 'Approve with changes' : 'Approve'}
          </Button>
        </div>
      </div>
    ) : mode === 'reject' ? (
      <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:justify-end">
        <Button
          type="button"
          variant="secondary"
          onClick={backToReview}
          disabled={isPending}
          className="whitespace-nowrap"
        >
          Back
        </Button>
        <Button
          type="submit"
          form="time-correction-reject-form"
          variant="danger"
          disabled={!rejectReason.trim()}
          loading={rejectMutation.isPending}
          className="whitespace-nowrap"
        >
          Reject request
        </Button>
      </div>
    ) : (
      <div className={FOOTER_ROW}>
        <p className={FOOTER_HINT}>
          {mayApprove
            ? 'Approving updates the attendance record with the requested times.'
            : 'Rejecting keeps the attendance record as it is.'}
        </p>
        {/* Phone: Approve spans the top row, Reject + Adjust share the one below. */}
        <div
          className={cn(
            'grid gap-2 sm:flex sm:shrink-0',
            mayApprove && mayReject ? 'grid-cols-2' : 'grid-cols-1'
          )}
        >
          {mayReject && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setMode('reject')}
              disabled={isPending}
              className="whitespace-nowrap"
            >
              Reject
            </Button>
          )}
          {mayApprove && (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={startAdjust}
                disabled={isPending}
                className="whitespace-nowrap"
              >
                <PencilLine className="h-3.5 w-3.5" aria-hidden />
                Adjust times
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleApprove}
                loading={approveMutation.isPending}
                disabled={isPending}
                className={cn(
                  'order-first whitespace-nowrap sm:order-0',
                  mayReject && 'col-span-2'
                )}
              >
                Approve
              </Button>
            </>
          )}
        </div>
      </div>
    )
  ) : (
    <div className="flex w-full justify-end">
      <Button type="button" variant="secondary" onClick={handleClose}>
        Close
      </Button>
    </div>
  )

  return (
    <Modal
      open={!!requestId}
      onClose={handleClose}
      title="Time correction request"
      className="max-w-2xl"
      footer={request ? footer : null}
    >
      {isLoading ? (
        <SectionLoader variant="detail" label="Loading time correction request" />
      ) : error ? (
        <QueryErrorState error={error} size="inline" title="Couldn't load this request" />
      ) : request ? (
        <div className="space-y-5">
          <RequesterHeader request={request} companyName={companyName} />
          <ChangeComparison request={request} />
          {canDecide && mode === 'adjust' && draft && (
            <AdjustTimesForm
              request={request}
              draft={draft}
              errors={draftErrors}
              onChange={(patch) => setDraft((d) => (d ? { ...d, ...patch } : d))}
              note={reviewNote}
              onNoteChange={setReviewNote}
            />
          )}
          <ReasonBlock request={request} />
          <ReviewOutcome request={request} />

          {canDecide && mode === 'reject' && (
            <form
              id="time-correction-reject-form"
              onSubmit={handleRejectSubmit}
              className="space-y-2 rounded-lg border border-red-200 bg-red-50/50 p-4 dark:border-red-500/30 dark:bg-red-500/5"
            >
              <label
                htmlFor="rejection-reason"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Why are you rejecting this? <span className="text-danger">*</span>
              </label>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {personDisplayName(getRequester(request), 'The employee')} will see this message.
              </p>
              <textarea
                id="rejection-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                required
                autoFocus
                className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                placeholder="e.g. Clock-in time doesn't match the shift log"
              />
            </form>
          )}
        </div>
      ) : null}
    </Modal>
  )
}

function RequesterHeader({
  request,
  companyName,
}: {
  request: TimeCorrectionRequest
  companyName?: string
}): React.ReactElement {
  const requester = getRequester(request)
  const name = personDisplayName(requester)
  const subline = userSubline(requester?.employeeId, requester?.email)
  const roleLine = personRoleLine(requester)
  const attendance = getOriginalAttendance(request)

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4 dark:border-gray-700 dark:bg-gray-800/40">
      <div className="flex items-start gap-3">
        <Avatar name={name} src={requester?.avatar} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">
              {name}
            </h3>
            <Badge variant={TIME_CORRECTION_STATUS_VARIANT[request.status]}>
              {TIME_CORRECTION_STATUS_LABEL[request.status]}
            </Badge>
          </div>
          {subline && (
            <p className="truncate text-sm text-gray-600 dark:text-gray-400">{subline}</p>
          )}
          {roleLine && (
            <p className="truncate text-sm text-gray-600 dark:text-gray-400">{roleLine}</p>
          )}
        </div>
      </div>
      <dl className="mt-3 grid gap-2 border-t border-gray-200 pt-3 text-sm sm:grid-cols-3 dark:border-gray-700">
        <MetaItem icon={Building2} label="Company" value={companyName ?? '—'} />
        <MetaItem
          icon={CalendarDays}
          label="Shift date"
          value={attendance?.date ? formatDate(attendance.date, 'EEE, d MMM yyyy') : '—'}
        />
        <MetaItem
          label="Submitted"
          value={request.createdAt ? formatRelative(request.createdAt) : '—'}
          title={request.createdAt ? formatDateTime(request.createdAt) : undefined}
        />
      </dl>
    </div>
  )
}

function MetaItem({
  icon: Icon,
  label,
  value,
  title,
}: {
  icon?: React.ComponentType<{ className?: string }>
  label: string
  value: string
  title?: string
}): React.ReactElement {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
        {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
        {label}
      </dt>
      <dd className="truncate font-medium text-gray-900 dark:text-gray-100" title={title}>
        {value}
      </dd>
    </div>
  )
}

function ChangeComparison({ request }: { request: TimeCorrectionRequest }): React.ReactElement {
  const original = getOriginalAttendance(request)
  const nextIn = request.requestedClockIn ?? original?.clockIn
  const nextOut = request.requestedClockOut ?? original?.clockOut
  const rows: Array<{
    label: string
    recorded: string
    requested: string
    changed: boolean
  }> = [
    {
      label: 'Clock-in',
      recorded: original?.clockIn ? formatDate(original.clockIn, CLOCK_FORMAT) : 'Not recorded',
      requested: request.requestedClockIn ? formatDate(request.requestedClockIn, CLOCK_FORMAT) : 'No change',
      changed: !!request.requestedClockIn,
    },
    {
      label: 'Clock-out',
      recorded: original?.clockOut ? formatDate(original.clockOut, CLOCK_FORMAT) : 'Not recorded',
      requested: request.requestedClockOut ? formatDate(request.requestedClockOut, CLOCK_FORMAT) : 'No change',
      changed: !!request.requestedClockOut,
    },
    {
      label: 'Shift length',
      recorded: formatMinutes(minutesBetween(original?.clockIn, original?.clockOut)),
      requested: formatMinutes(minutesBetween(nextIn, nextOut)),
      changed: !!(request.requestedClockIn || request.requestedClockOut),
    },
  ]

  return (
    <section aria-labelledby="tc-changes-heading">
      <h4
        id="tc-changes-heading"
        className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400"
      >
        Requested changes
      </h4>
      {/* Phone: one stacked row per field; the four-column table is too wide below sm. */}
      <dl className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 text-sm sm:hidden dark:divide-gray-700 dark:border-gray-700">
        {rows.map((row) => (
          <div key={row.label} className="space-y-1 px-3 py-2.5">
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">{row.label}</dt>
            <dd className="flex flex-wrap items-center gap-x-2 gap-y-0.5 tabular-nums">
              <span
                className={cn(
                  'text-gray-600 dark:text-gray-400',
                  row.changed && 'line-through decoration-gray-400/70'
                )}
              >
                {row.recorded}
              </span>
              {row.changed && (
                <>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
                  <span className="sr-only">changed to</span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {row.requested}
                  </span>
                </>
              )}
            </dd>
          </div>
        ))}
      </dl>
      <div className="hidden overflow-hidden rounded-lg border border-gray-200 sm:block dark:border-gray-700">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs text-gray-500 dark:bg-gray-800/60 dark:text-gray-400">
              <th scope="col" className="px-3 py-2 font-medium"><span className="sr-only">Field</span></th>
              <th scope="col" className="px-3 py-2 font-medium">Recorded</th>
              <th scope="col" className="w-6 px-0 py-2" aria-hidden />
              <th scope="col" className="px-3 py-2 font-medium">Requested</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-t border-gray-200 dark:border-gray-700">
                <th scope="row" className="px-3 py-2.5 text-left font-medium text-gray-700 dark:text-gray-300">
                  {row.label}
                </th>
                <td
                  className={cn(
                    'px-3 py-2.5 tabular-nums text-gray-600 dark:text-gray-400',
                    row.changed && 'line-through decoration-gray-400/70'
                  )}
                >
                  {row.recorded}
                </td>
                <td className="px-0 py-2.5 text-gray-400" aria-hidden>
                  {row.changed && <ArrowRight className="h-3.5 w-3.5" />}
                </td>
                <td
                  className={cn(
                    'px-3 py-2.5 tabular-nums',
                    row.changed
                      ? 'font-semibold text-gray-900 dark:text-gray-100'
                      : 'text-gray-400 dark:text-gray-500'
                  )}
                >
                  {row.requested}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {request.requestedBreaks && request.requestedBreaks.length > 0 && (
        <div className="mt-3 text-sm">
          <p className="mb-1 font-medium text-gray-700 dark:text-gray-300">Requested breaks</p>
          <ul className="space-y-1 text-gray-600 dark:text-gray-400">
            {request.requestedBreaks.map((b, i) => (
              <li key={`${b.startTime}-${i}`} className="tabular-nums">
                {formatDate(b.startTime, 'p')}
                {b.endTime ? ` – ${formatDate(b.endTime, 'p')}` : ''}
                {' · '}
                {b.isPaid ? 'Paid' : 'Unpaid'}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

function ReasonBlock({ request }: { request: TimeCorrectionRequest }): React.ReactElement {
  const code = reasonCodeLabel(request.reasonCode)
  return (
    <section aria-labelledby="tc-reason-heading">
      <div className="mb-2 flex items-center gap-2">
        <h4
          id="tc-reason-heading"
          className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400"
        >
          Employee's reason
        </h4>
        {code && <Badge variant="neutral">{code}</Badge>}
      </div>
      <div className="flex gap-2 rounded-lg border border-gray-200 p-3 text-sm text-gray-900 dark:border-gray-700 dark:text-gray-100">
        <Quote className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        <p className="whitespace-pre-line">{request.reason}</p>
      </div>
    </section>
  )
}

function ReviewOutcome({ request }: { request: TimeCorrectionRequest }): React.ReactElement | null {
  if (request.status === 'pending') return null
  const approved = request.status === 'approved'
  const reviewer = getReviewer(request)
  const Icon = approved ? CheckCircle2 : XCircle
  return (
    <section
      className={cn(
        'flex gap-3 rounded-lg border p-3 text-sm',
        approved
          ? 'border-green-200 bg-green-50/60 dark:border-green-500/30 dark:bg-green-500/5'
          : 'border-red-200 bg-red-50/60 dark:border-red-500/30 dark:bg-red-500/5'
      )}
    >
      <Icon
        className={cn('mt-0.5 h-4 w-4 shrink-0', approved ? 'text-green-600' : 'text-red-600')}
        aria-hidden
      />
      <div className="space-y-1">
        <p className="text-gray-900 dark:text-gray-100">
          <span className="font-medium">{approved ? 'Approved' : 'Rejected'}</span>
          {reviewer && <> by {personDisplayName(reviewer)}</>}
          {request.reviewedAt && <> on {formatDateTime(request.reviewedAt)}</>}
        </p>
        {request.rejectionReason && (
          <p className="text-gray-700 dark:text-gray-300">{request.rejectionReason}</p>
        )}
        {approved && (request.approvedClockIn || request.approvedClockOut) && (
          <ul className="space-y-0.5 text-gray-700 dark:text-gray-300">
            {request.approvedClockIn && (
              <AdjustedLine
                label="Clock-in"
                approved={request.approvedClockIn}
                requested={request.requestedClockIn}
              />
            )}
            {request.approvedClockOut && (
              <AdjustedLine
                label="Clock-out"
                approved={request.approvedClockOut}
                requested={request.requestedClockOut}
              />
            )}
          </ul>
        )}
        {request.reviewNote && (
          <p className="text-gray-700 dark:text-gray-300">{request.reviewNote}</p>
        )}
      </div>
    </section>
  )
}

function AdjustedLine({
  label,
  approved,
  requested,
}: {
  label: string
  approved: string
  requested?: string
}): React.ReactElement {
  return (
    <li className="tabular-nums">
      {label} set to <span className="font-medium">{formatDate(approved, CLOCK_FORMAT)}</span>
      {requested && (
        <span className="text-gray-500 dark:text-gray-400">
          {' '}
          (requested {formatDate(requested, CLOCK_FORMAT)})
        </span>
      )}
    </li>
  )
}

function AdjustTimesForm({
  request,
  draft,
  errors,
  onChange,
  note,
  onNoteChange,
}: {
  request: TimeCorrectionRequest
  draft: ClockDraft
  errors: { in?: string; out?: string }
  onChange: (patch: Partial<ClockDraft>) => void
  note: string
  onNoteChange: (value: string) => void
}): React.ReactElement {
  const initial = initialDraft(request)
  const { clockIn: requestedIn, clockOut: requestedOut } = effectiveTimes(request)
  const nextIn = joinLocal(draft.inDate, draft.inTime)
  const nextOut = joinLocal(draft.outDate, draft.outTime)
  const length =
    nextIn && nextOut && !errors.in && !errors.out
      ? minutesBetween(nextIn.toISOString(), nextOut.toISOString())
      : null
  const employee = personDisplayName(getRequester(request), 'The employee')

  const rows = [
    {
      key: 'in' as const,
      label: 'Clock-in',
      date: draft.inDate,
      time: draft.inTime,
      was: requestedIn,
      changed: draft.inDate !== initial.inDate || draft.inTime !== initial.inTime,
      error: errors.in,
      patch: (date: string, time: string) => onChange({ inDate: date, inTime: time }),
    },
    {
      key: 'out' as const,
      label: 'Clock-out',
      date: draft.outDate,
      time: draft.outTime,
      was: requestedOut,
      changed: draft.outDate !== initial.outDate || draft.outTime !== initial.outTime,
      error: errors.out,
      patch: (date: string, time: string) => onChange({ outDate: date, outTime: time }),
    },
  ]

  return (
    <section
      aria-labelledby="tc-adjust-heading"
      className="space-y-4 rounded-lg border border-primary/25 bg-primary/[0.03] p-4 dark:border-primary/30 dark:bg-primary/5"
    >
      <div>
        <h4 id="tc-adjust-heading" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Approve with these times
        </h4>
        <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
          Filled in with the request. {employee} will see what you changed.
        </p>
      </div>

      <div className="space-y-3">
        {rows.map((row) => (
          <div key={row.key} className={CLOCK_FIELD_GRID}>
            <div className="sm:pt-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{row.label}</span>
              {row.changed && (
                <p className="text-xs tabular-nums text-gray-500 line-through decoration-gray-400/70 dark:text-gray-400">
                  {row.was ? formatDate(row.was, 'd MMM, p') : 'Not set'}
                </p>
              )}
            </div>
            <div className="min-w-0">
              <DateInput
                value={row.date}
                onChange={(e) => row.patch(e.target.value, row.time)}
                aria-label={`${row.label} date`}
                aria-invalid={!!row.error}
              />
              {row.error && <p className="mt-1 text-xs text-danger">{row.error}</p>}
            </div>
            <TimeInput
              value={row.time}
              onChange={(v) => row.patch(row.date, v)}
              id={`tc-adjust-${row.key}-time`}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between border-t border-primary/15 pt-3 text-sm dark:border-primary/20">
        <span className="text-gray-600 dark:text-gray-400">Shift length</span>
        <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">
          {formatMinutes(length)}
        </span>
      </div>

      <div>
        <label
          htmlFor="tc-review-note"
          className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Note to {employee} <span className="font-normal text-gray-500">(optional)</span>
        </label>
        <textarea
          id="tc-review-note"
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          rows={2}
          maxLength={500}
          className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          placeholder="e.g. Shift log shows you clocked out at 03:30"
        />
      </div>
    </section>
  )
}

TimeCorrectionRequestDetailModal.displayName = 'TimeCorrectionRequestDetailModal'
